import React, { useEffect, useMemo, useState } from 'react';
import {
    ProviderClaimTransmission,
    SupportClaimTransmission,
    TooltipTransitionComponent,
} from '../../../..';
import { mdiRomanNumeral1, mdiRomanNumeral2, mdiRomanNumeral3 } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import { ITransmissionDetailProps } from './transmission-detail.types';
import { TransmissionEmbeddedPanel } from './transmission-detail-embedded';
import * as S from '../claim-timeline.styles';

export interface ITransmissionWithPaymentIds {
    paymentIds?: string[];
}

export const TransmissionStatus: React.FunctionComponent<{
    transmission: ITransmissionWithPaymentIds;
}> = ({ transmission }) => {
    if (!transmission.paymentIds || transmission.paymentIds.length === 0) {
        return (
            <Tooltip
                placement="top"
                title="Primary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral1} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }
    if (transmission.paymentIds.length === 1) {
        return (
            <Tooltip
                placement="top"
                title="Secondary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral2} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }
    if (transmission.paymentIds.length === 2) {
        return (
            <Tooltip
                placement="top"
                title="Tertiary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral3} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }
    return null;
};

export const TransmissionDetail: React.FunctionComponent<ITransmissionDetailProps> = (props) => {
    const {
        transmission,
        appType,
        previousTransmission,
        downloadDocument,
        getClearinghousePayer,
        getPayerSubscriber,
        embedded = false,
    } = props;

    const showDiff = !!previousTransmission?.x12 && !!transmission.x12;

    const [clearinghousePayer, setClearinghousePayer] = useState<any>();
    const [payer, setPayer] = useState<any>();
    const [subscriber, setSubscriber] = useState<any>();
    const [documentUrl, setDocumentUrl] = useState<{ url?: string } | undefined>();
    const [loadingPayer, setLoadingPayer] = useState(false);
    const [documentLoading, setDocumentLoading] = useState(false);

    const documentId = transmission.documentId;

    useEffect(() => {
        if (appType !== 'support' || !getClearinghousePayer) return;
        const id = (transmission as SupportClaimTransmission).clearinghousePayerId;
        if (!id) return;
        setLoadingPayer(true);
        getClearinghousePayer(id)
            .then((res) => {
                if (res) setClearinghousePayer(res);
            })
            .finally(() => setLoadingPayer(false));
    }, [appType, transmission, getClearinghousePayer]);

    useEffect(() => {
        if (appType !== 'provider' || !getPayerSubscriber) return;
        const id = (transmission as ProviderClaimTransmission).payerSubscriberId;
        if (!id) return;
        setLoadingPayer(true);
        getPayerSubscriber(id)
            .then((res) => {
                if (res) {
                    setPayer(res.payer);
                    setSubscriber(res.subscriber);
                }
            })
            .finally(() => setLoadingPayer(false));
    }, [appType, transmission, getPayerSubscriber]);

    useEffect(() => {
        if (!documentId) return;
        setDocumentLoading(true);
        downloadDocument(documentId)
            .then(setDocumentUrl)
            .finally(() => setDocumentLoading(false));
    }, [documentId, downloadDocument]);

    const sortedStatuses = useMemo(
        () =>
            (transmission.statuses || []).slice().sort(
                (a, b) => _.toNumber(b.externalId) - _.toNumber(a.externalId)
            ),
        [transmission.statuses]
    );

    return (
        <TransmissionEmbeddedPanel
            transmission={transmission}
            appType={appType}
            previousTransmission={previousTransmission}
            clearinghousePayer={clearinghousePayer}
            payer={payer}
            subscriber={subscriber}
            loadingPayer={loadingPayer}
            sortedStatuses={sortedStatuses}
            showDiff={showDiff}
            documentLoading={documentLoading}
            documentUrl={documentUrl}
        />
    );
};
